import { useMutation } from '@apollo/client';
import React, { useEffect } from 'react';
import { Button } from '../../../../components/Button/Button';
import { Card } from '../../../../components/Card/Card';
import { FilterBar } from '../../../../components/FilterBar/FilterBar';
import { Icon } from '../../../../components/Icon/Icon';
import { useModalPresenter } from '../../../../components/Modal/PresentedModal';
import { SortableList } from '../../../../components/SortableList/SortableList';
import { Stack } from '../../../../components/Stack/Stack';
import { graphql } from '../../../../generated';
import { t } from '../../../../locales/translate';
import { withSuspendedCard } from '../../../../util/suspense';
import { useParams } from 'react-router-dom';
import { VoucherCodeCreate } from './create';
import { useQueryAsState } from '../../../../model/history';
import { VoucherCodeUpdate } from './update';
import { useConfirmation } from '../../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { QueryNames } from '../../../../model/graphql';
import { useSelection } from '../../../../util/react-util';
import { VoucherCodeImport } from './import';
import { handleError } from '../../../../model/notification';
import { runVoucherCodesExport } from '../../../../model/exports';
import { useSuspenseQuery } from '../../../../util/use_suspense_query';
import { Spinner } from '../../../../components/Spinner/Spinner';

export const VOUCHER_CODES = graphql(`
  query VoucherCodes($id: ID!, $after: String, $first: Int, $terms: [String!]) {
    voucherCampaign(id: $id) {
      id
      voucherCodesCount
      voucherCodes(after: $after, first: $first, terms: $terms) {
        edges {
          node {
            id
            code
            ordersCount
            voucherCodeUsesCount
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        permissions {
          id
          create
        }
      }
    }
  }
`);

const DESTROY_VOUCHER_CODES = graphql(`
  mutation DestroyVoucherCodes($ids: [ID!]!) {
    destroyVoucherCodes(input: { ids: $ids }) {
      clientMutationId
    }
  }
`);

const DESTROY_ALL_VOUCHER_CODES = graphql(`
  mutation DestroyAllVoucherCodes($voucherCampaignId: ID!) {
    destroyAllVoucherCodes(input: { voucherCampaignId: $voucherCampaignId }) {
      clientMutationId
    }
  }
`);

function VoucherCodesList$(): JSX.Element {
  const translation = t.page.voucherCampaign.voucherCodes;
  const { id } = useParams<{ id: string }>();
  const [params] = useQueryAsState();

  const { data, loadingMore, fetchMore } = useSuspenseQuery(VOUCHER_CODES, {
    variables: {
      id,
      terms: params.query?.map((item) => `${item.exclude ? '-' : ''}${item.label}`),
      first: 10,
    },
  });

  const modal = useModalPresenter();

  const voucherCodes = data.voucherCampaign.voucherCodes.edges.map((edge) => edge.node);

  const { pageInfo, permissions } = data.voucherCampaign.voucherCodes;

  // Handle Selection
  const { allSelected, values, size, toggle, toggleAll, clear, selectAll } = useSelection<string>();
  const selectedItems = voucherCodes.filter((item) => values.includes(item.id));

  const [destroy] = useMutation(DESTROY_VOUCHER_CODES, { refetchQueries: [QueryNames.VOUCHER_CODES] });
  const [destroyAll] = useMutation(DESTROY_ALL_VOUCHER_CODES, { refetchQueries: [QueryNames.VOUCHER_CODES] });

  let confirmTranslation = translation.delete.single;
  if (size > 1) {
    confirmTranslation = translation.delete.multiple;
  }
  if (allSelected) {
    confirmTranslation = translation.delete.all;
  }

  const { confirm } = useConfirmation({
    title: confirmTranslation.title,
    message: confirmTranslation.description,
    actionName: translation.delete.confirm,
    action: async () => {
      if (allSelected) {
        await destroyAll({
          variables: {
            voucherCampaignId: id,
          },
        });
        clear();
        return;
      }

      if (size === 0) return;
      await destroy({
        variables: {
          ids: values,
        },
      });
      clear();
    },
  });

  async function handleDownloadCodes(): Promise<void> {
    try {
      await runVoucherCodesExport({ voucherCampaignId: id });
    } catch (error) {
      handleError(error);
    }
  }

  // Handle Header
  const exportCodes = voucherCodes.length ? (
    <Icon tooltip={translation.downloadCodes} name="download" onClick={handleDownloadCodes} />
  ) : undefined;

  let cardTitle = translation.voucherCodes;
  let cardActions = (
    <>
      {exportCodes}
      <Icon
        tooltip={translation.import.importCsv}
        name="share"
        onClick={() => modal.present(VoucherCodeImport, { id }, { size: 'small' })}
        disabled={!permissions.create}
      />
      <Icon
        name="plus"
        onClick={() => modal.present(VoucherCodeCreate, { voucherCampaignId: id }, { size: 'small' })}
        disabled={!permissions.create}
      />
    </>
  );

  if (size > 0) {
    if (size === 1) {
      cardTitle = translation.singleResultSelected;
    } else {
      cardTitle = translation.manyResultsSelected(size);
    }
    cardActions = (
      <>
        {!allSelected && (
          <Button
            title={translation.selectAll}
            type="secondary-transparent"
            condensed
            onClick={() => selectAll(voucherCodes.map((item) => item.id))}
          />
        )}
        <Icon name="trash" onClick={confirm} disabled={!permissions.create} />
      </>
    );
  }

  if (allSelected) {
    cardTitle = translation.allSelected;
  }

  // Check all incoming voucher codes from pagination if all selected
  useEffect(() => {
    if (allSelected) {
      selectAll(voucherCodes.map((item) => item.id));
    }
  }, [voucherCodes.length]);

  return (
    <Card
      title={cardTitle}
      actions={cardActions}
      withSelectedItems={size > 0}
      fullbleed
      footer={
        loadingMore ? (
          <Spinner />
        ) : (
          pageInfo?.hasNextPage && (
            <Button
              title={t.actions.loadMore}
              onClick={() => {
                fetchMore({
                  variables: {
                    after: pageInfo.endCursor,
                  },
                });
              }}
            />
          )
        )
      }
    >
      <SortableList
        items={voucherCodes}
        emptyMessage={translation.noVoucherCodes}
        multiSelect
        selectedItems={selectedItems}
        onRowSelect={(row) => toggle(row.id)}
        onSelectAll={() => toggleAll(voucherCodes.map((item) => item.id))}
        actions={
          <Stack line align="start">
            <FilterBar filters={['query']} queryStyle={{ minWidth: '250px' }} />
          </Stack>
        }
        columns={[
          {
            label: translation.code,
            dataKey: 'code',
          },
          {
            label: translation.totalUsed,
            dataKey: 'voucherCodeUsesCount',
          },
          {
            dataKey: 'id',
            cellRenderer: (row) => {
              return (
                <Button
                  icon="edit"
                  type="icon-simple"
                  onClick={() => modal.present(VoucherCodeUpdate, { id: row.id }, { size: 'small' })}
                />
              );
            },
          },
        ]}
      />
    </Card>
  );
}

export const VoucherCodesList = withSuspendedCard(VoucherCodesList$, {
  loadingTitle: t.page.voucherCampaigns.voucherCampaigns,
});
